import {
  ProviderUnavailableError,
  type FrameInterpolationProvider,
  type FrameInterpolationRequest,
  type FrameInterpolationResult,
  type ProviderCallOptions,
  type ProviderFrameInterpolationJobState,
  type ProviderJobHandle,
} from "@loumy/contracts";
import { streamFalStatus } from "./fal-status-stream";
import { safeFetchBytes } from "./safe-fetch";

// §7 `frame-interpolation`-Provider → **RIFE** (Real-Time Intermediate Flow Estimation) über
// die fal.ai-Queue-API — NEUER Slot-DEFAULT per D122-Korrektur (User: „ich habe RIFE gesagt,
// womit ich RIFE meine"): interpoliert die D122-Loop-Naht (GENAU 1 Zwischenframe neuer
// End-Frame → Start-Frame nach dem Duplikat-Drop). FILM (`fal-film.ts`) bleibt registrierter
// SWAP — die D94/D99-Alt-Spec-Pins lösen ihn weiter NAMENTLICH auf (D32).
// Gleicher FAL_API_KEY wie video/image/vision/image-edit (EIN fal-Vertrag).
//
// Modell-Contract `fal-ai/rife` (OpenAPI verifiziert 2026-06-06):
//   start_image_url (REQUIRED), end_image_url (REQUIRED), num_frames (int 1–64, Default 1),
//   output_type ∈ images|video (wir: images), output_format ∈ png|jpeg (wir: png — verlustfrei,
//   die Naht-Frames werden downstream chroma-gekeyt; ACHTUNG: Feld heißt hier `output_format`,
//   bei FILM `image_format`), include_start/include_end (bool, Default false — passt: NUR die
//   Zwischenframes, Start/Ende existieren bereits in der Frame-Sequenz).
//   Output (images): { images: [{ url }] } in zeitlicher Reihenfolge start→end.
//
// ZWEI Oberflächen (D167): `interpolate` = Einzelaufruf wie FalFilmProvider (SSE-Stream primär,
// 5-s-Poll-Fallback, D106 — für die D122/D158-Naht: EIN Call je Job); `generate`/`poll` =
// D27-Handle-Oberfläche (Submit→Handle, Status-GET non-blocking — für frame-smooth: bis 24
// sequenzielle Paare je Knoten, per-Paar-Resume macht die Schleife tick-sicher, kein Re-Submit/
// Re-COGS nach Pause). `opts.signal` bricht in beiden ab.

const FAL_QUEUE_BASE_URL = "https://queue.fal.run";
const FAL_RIFE_MODEL = "fal-ai/rife";
const POLL_INTERVAL_MS = 5000; // D106: Fallback-Polling — 5 s
const MAX_POLLS = 36; // Fallback-Deckel ~3 min; RIFE ist real deutlich schneller
// Größen-Cap je Ergebnis-PNG (DoS-/Speicher-Schutz) — wie fal-film.ts.
const MAX_FRAME_BYTES = 16 * 1024 * 1024;
// D122: der D94/D122-Pfad fordert 1 Naht-Frame an. D158: die RIFE-Loop-Naht fordert bis zu 8
// Brücken-Frames (= seamFrames-Regler-Max, = frames-append-Cap) ⇒ Cap auf 8 angehoben (defensiv,
// fal-Doku erlaubt num_frames 1–64; mehr braucht die Naht nie).
const MAX_NUM_FRAMES = 8;

export interface FalRifeConfig {
  apiKey?: string;
  baseUrl?: string;
  /** Modell-Override (Default `fal-ai/rife`). */
  model?: string;
}

/** Transienter Result-GET-Flap (429/5xx) nach COMPLETED — `poll()` mappt auf `processing`. */
class RifeResultTransientError extends Error {}

/** sleep, das auf opts.signal reagiert (Cancel/Timeout bricht sofort ab). */
function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new Error("aborted"));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new Error("aborted"));
      },
      { once: true },
    );
  });
}

/**
 * §7 frame-interpolation → RIFE über fal.ai (D122-Korrektur). `interpolate({startImageUrl,
 * endImageUrl, numFrames})` → submit → SSE-Stream/Poll bis COMPLETED → Ergebnis-PNGs laden
 * (safeFetchBytes, größen-gedeckelt) → Bytes in zeitlicher Reihenfolge.
 */
export class FalRifeProvider implements FrameInterpolationProvider {
  readonly id = "fal-rife";
  readonly version = 1;
  readonly displayName = "fal.ai (RIFE Frame-Interpolation)";

  private readonly baseUrl: string;
  private readonly model: string;

  constructor(private readonly config: FalRifeConfig = {}) {
    this.baseUrl = (config.baseUrl?.trim() || FAL_QUEUE_BASE_URL).replace(/\/+$/, "");
    this.model = config.model?.trim() || FAL_RIFE_MODEL;
  }

  private requireKey(): string {
    if (!this.config.apiKey)
      throw new ProviderUnavailableError("frame-interpolation", this.id, "FAL_API_KEY fehlt (Adapter nicht konfiguriert, §13).");
    return this.config.apiKey;
  }

  private headers(apiKey: string, json: boolean): Record<string, string> {
    const h: Record<string, string> = { Authorization: `Key ${apiKey}` };
    if (json) h["Content-Type"] = "application/json";
    return h;
  }

  /** fal-Queue-URLs gegen den fal-Host validieren (SSRF/Drift, wie die übrigen fal-Adapter). */
  private requireFalUrl(url: unknown, field: string): string {
    if (typeof url !== "string" || url.length === 0) throw new Error(`fal rife: '${field}' fehlt.`);
    let host: string;
    try {
      host = new URL(url).origin;
    } catch {
      throw new Error(`fal rife: '${field}' ist keine gültige URL.`);
    }
    if (host !== new URL(this.baseUrl).origin)
      throw new Error(`fal rife: '${field}' (${host}) liegt nicht auf dem fal-Host (${new URL(this.baseUrl).origin}).`);
    return url;
  }

  /** COMPLETED → Ergebnis-PNGs laden (gemeinsamer Endpfad von Stream- und Poll-Weg, D106).
   *  Transienter Result-GET-Flap (429/5xx) → RifeResultTransientError: `poll()` mappt ihn auf
   *  `processing` (Retry im nächsten Poll, esrgan-Parität/D141-Download-Flap-Lehre); im
   *  synchronen `interpolate` propagiert er wie bisher als Fehler. */
  private async fetchResult(
    responseUrl: string,
    apiKey: string,
    numFrames: number,
    requestId: string,
    signal?: AbortSignal,
  ): Promise<FrameInterpolationResult> {
    const resultRes = await fetch(responseUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!resultRes.ok) {
      if (resultRes.status === 429 || resultRes.status >= 500)
        throw new RifeResultTransientError(`fal rife result: HTTP ${resultRes.status} (transient)`);
      throw new Error(`fal rife result: HTTP ${resultRes.status} ${resultRes.statusText}`);
    }
    const result = (await resultRes.json()) as { images?: { url?: unknown }[] };
    const urls = (result.images ?? []).map((im) => im?.url).filter((u): u is string => typeof u === "string");
    if (urls.length === 0) throw new Error("fal rife: COMPLETED, aber keine images[].url im Ergebnis.");
    // Frame-Anzahl VOR den Downloads deckeln: ein malformter Provider-Response darf nicht
    // mehr als die angeforderte/gedeckelte Zahl 16-MB-Downloads erzwingen (FILM-Codex-P2).
    if (urls.length > Math.max(numFrames, 1))
      throw new Error(`fal rife: ${urls.length} Frames im Ergebnis, angefordert ${numFrames} (malformter Response).`);
    const frames: Uint8Array[] = [];
    for (const url of urls) {
      const { bytes } = await safeFetchBytes(url, { maxBytes: MAX_FRAME_BYTES, signal });
      frames.push(bytes);
    }
    return {
      frames,
      provenance: { provider: this.id, modelVersion: this.model, requestId, seed: null },
    };
  }

  /** Gemeinsamer Submit (interpolate + generate): POST → request_id/status_url/response_url. */
  private async submitJob(
    req: FrameInterpolationRequest,
    apiKey: string,
    signal?: AbortSignal,
  ): Promise<{ requestId: string; statusUrl: string; responseUrl: string; numFrames: number }> {
    // Codex-P1: nicht-finite numFrames (NaN/undefined-Durchrutscher) VOR dem Clamp auf 1
    // coercen — sonst ginge `num_frames: null` ins JSON und der Frame-Count-Guard im
    // fetchResult wäre via Math.max(NaN, 1) wirkungslos.
    const requested = Number.isFinite(req.numFrames) ? req.numFrames : 1;
    const numFrames = Math.max(1, Math.min(MAX_NUM_FRAMES, Math.round(requested)));

    const submit = await fetch(`${this.baseUrl}/${this.model}`, {
      method: "POST",
      headers: this.headers(apiKey, true),
      body: JSON.stringify({
        start_image_url: req.startImageUrl,
        end_image_url: req.endImageUrl,
        num_frames: numFrames,
        output_type: "images",
        output_format: "png", // verlustfrei — die Naht-Frames werden downstream chroma-gekeyt
        include_start: false, // NUR Zwischenframes: Start/Ende existieren in der Sequenz bereits
        include_end: false,
      }),
      signal,
    });
    if (!submit.ok) throw new Error(`fal rife submit: HTTP ${submit.status} ${submit.statusText}`);
    const sub = (await submit.json()) as { request_id?: string; status_url?: unknown; response_url?: unknown };
    if (!sub.request_id) throw new Error("fal rife submit: keine request_id in der Antwort.");
    return {
      requestId: sub.request_id,
      statusUrl: this.requireFalUrl(sub.status_url, "status_url"),
      responseUrl: this.requireFalUrl(sub.response_url, "response_url"),
      numFrames,
    };
  }

  /** D167/D27: NUR submitten → Handle (frame-smooth-per-Paar-Resume). `opts.signal` bricht ab. */
  async generate(req: FrameInterpolationRequest, opts?: ProviderCallOptions): Promise<ProviderJobHandle> {
    const apiKey = this.requireKey();
    const { requestId, statusUrl, responseUrl, numFrames } = await this.submitJob(req, apiKey, opts?.signal);
    // numFrames reist im Handle mit: der Frame-Count-Guard in fetchResult braucht ihn beim
    // Resume-Poll (das Handle round-trippt als jsonb durch die DB, D27).
    return { providerId: this.id, providerVersion: this.version, externalId: requestId, raw: { statusUrl, responseUrl, numFrames } };
  }

  /** D167/D27: EIN Status-GET (non-blocking). IN_QUEUE→pending, IN_PROGRESS→processing,
   *  COMPLETED→PNGs laden→done; 429/5xx-Status-Flap→processing (transient, wie fal-esrgan). */
  async poll(handle: ProviderJobHandle, opts?: ProviderCallOptions): Promise<ProviderFrameInterpolationJobState> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const raw = (handle.raw ?? {}) as { statusUrl?: unknown; responseUrl?: unknown; numFrames?: unknown };
    // SSRF/Drift: die URLs round-trippen durch die DB — beim Poll erneut host-validieren.
    const statusUrl = this.requireFalUrl(raw.statusUrl, "status_url (handle)");
    const responseUrl = this.requireFalUrl(raw.responseUrl, "response_url (handle)");
    const numFrames = typeof raw.numFrames === "number" && Number.isFinite(raw.numFrames) ? raw.numFrames : 1;

    const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
    if (!statusRes.ok) {
      if (statusRes.status === 429 || statusRes.status >= 500) return { status: "processing" };
      const body = (await statusRes.text().catch(() => "")).slice(0, 500);
      return { status: "failed", error: `fal rife status: HTTP ${statusRes.status} ${statusRes.statusText}${body ? ` — ${body}` : ""}` };
    }
    const status = (await statusRes.json()) as { status?: string; error?: string };
    if (status.error) return { status: "failed", error: `fal rife: ${status.error}` };
    switch (status.status) {
      case "COMPLETED":
        try {
          return { status: "done", output: await this.fetchResult(responseUrl, apiKey, numFrames, handle.externalId, signal) };
        } catch (e) {
          // Transienter Result-Flap (429/5xx) → im nächsten Poll erneut versuchen (esrgan-Parität).
          if (e instanceof RifeResultTransientError) return { status: "processing" };
          throw e;
        }
      case "IN_QUEUE":
        return { status: "pending" };
      case "IN_PROGRESS":
        return { status: "processing" };
      default:
        return { status: "failed", error: `fal rife: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).` };
    }
  }

  async interpolate(req: FrameInterpolationRequest, opts?: ProviderCallOptions): Promise<FrameInterpolationResult> {
    const apiKey = this.requireKey();
    const signal = opts?.signal;
    const { requestId, statusUrl, responseUrl, numFrames } = await this.submitJob(req, apiKey, signal);
    // D106 PRIMÄR: SSE-Status-Stream (EIN Subrequest bis zum terminalen Status).
    const terminal = await streamFalStatus({ statusUrl, apiKey, signal });
    if (terminal !== null) {
      if (terminal.error !== undefined) throw new Error(`fal rife: ${terminal.error}`);
      if (terminal.status === "COMPLETED") return this.fetchResult(responseUrl, apiKey, numFrames, requestId, signal);
      throw new Error(`fal rife: unerwarteter Status '${terminal.status ?? "—"}' (terminal ohne error-Feld?).`);
    }

    // D106 FALLBACK (Stream nicht verfügbar/Timeout/Netzabbruch): Polling alle 5 s.
    for (let i = 0; i < MAX_POLLS; i++) {
      if (signal?.aborted) throw new Error("fal rife: aborted (Job-Timeout/Cancel).");
      const statusRes = await fetch(statusUrl, { method: "GET", headers: this.headers(apiKey, false), signal });
      if (statusRes.ok) {
        const status = (await statusRes.json()) as { status?: string; error?: string };
        if (status.error) throw new Error(`fal rife: ${status.error}`);
        if (status.status === "COMPLETED") return this.fetchResult(responseUrl, apiKey, numFrames, requestId, signal);
        if (status.status !== "IN_QUEUE" && status.status !== "IN_PROGRESS")
          throw new Error(`fal rife: unerwarteter Status '${status.status ?? "—"}' (terminal ohne error-Feld?).`);
      } else if (statusRes.status !== 429 && statusRes.status < 500) {
        throw new Error(`fal rife status: HTTP ${statusRes.status} ${statusRes.statusText}`);
      }
      await sleep(POLL_INTERVAL_MS, signal);
    }
    throw new Error(`fal rife: nicht fertig nach ${MAX_POLLS} Polls (~${(MAX_POLLS * POLL_INTERVAL_MS) / 1000}s).`);
  }
}
